<script lang="ts">
    import type { Event } from '../../api/events';
    import * as t from '../../translations/Events.ts';
    import { getEventDescriptionExcerpt, getFriendlyTimeLabel, isEventRegistrationFull } from '../../api/events';
    import CaretRightIcon from '../../icons/CaretRightIcon.svelte';
    
    export let event: Event;
    export let onRegister: (event: Event) => void = () => {};
    
    function formatDate(dateString: string | undefined, isAllDayEvent?: boolean): string {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            ...(isAllDayEvent ? { timeZone: 'UTC' } : {})
        });
    }

    function formatTime(dateString: string | undefined): string {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return '';
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit'
        });
    }

    function handleCardKeydown(evt: KeyboardEvent): void {
        if (evt.currentTarget !== evt.target) return;
        if (evt.key === 'Enter' || evt.key === ' ') {
            evt.preventDefault();
            onRegister(event);
        }
    }

    $: dateTimeLabel = event.isAllDayEvent
        ? formatDate(event.startDate, true)
        : `${formatDate(event.startDate)} • ${formatTime(event.startDate)}`;
    $: friendlyTime = getFriendlyTimeLabel(event.startDate);
    $: registrationFull = isEventRegistrationFull(event);
    $: showAvailableSlots = !registrationFull
        && !event.isRegistered
        && event.availableSlots != null
        && event.maxRegistrations != null
        && event.maxRegistrations > 0;
</script>

<!--
  Whole card is the click/keyboard target -- mirrors PetitionCard so the
  visual focus ring only appears on keyboard navigation (focus-visible) and
  not on mouse click. `group` lets the inner CTA pill follow the card's
  hover/active state via group-hover/group-active rather than each element
  hooking its own listeners. `transform-gpu` + `will-change-transform` keeps
  the subtle 1.005 scale jank-free on lower-end devices.
-->
<div 
    class="event-card-shell group flex flex-col md:flex-row border border-gray-300 border-l border-l-gray-300 bg-white overflow-hidden h-auto rounded-lg transition-[transform,box-shadow,border-color,border-width] duration-300 ease-in-out transform-gpu origin-left will-change-transform hover:scale-[1.005] hover:shadow-lg hover:border-l-4 hover:border-l-[var(--vv-action-center-button-color,#0d9488)] active:border-l-4 active:border-l-[var(--vv-action-center-button-color,#0d9488)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vv-action-center-button-color,#0d9488)] focus-visible:ring-offset-2 active:outline-none cursor-pointer {event.imageUrl ? 'md:h-[250px]' : 'md:h-[170px]'}"
    data-testid={`event-${event.id}`}
    data-registered={event.isRegistered ? 'true' : undefined}
    role="button"
    tabindex="0"
    aria-label={event.isRegistered ? `View registration: ${event.title}` : `Register for event: ${event.title}`}
    on:click={() => onRegister(event)}
    on:keydown={handleCardKeydown}
>
    <!-- Left area - image or title/date -->
    <div class="w-full md:w-2/5 flex-shrink-0 {event.imageUrl ? '' : 'hidden md:block md:border-r md:border-gray-200'}">
        {#if event.imageUrl}
            <img 
                src={event.imageUrl} 
                alt={event.title}
                class="w-full h-[160px] md:h-full object-cover"
                data-testid="image"
            />
        {:else}
            <!-- Desktop only: title/date on left side -->
            <div class="p-4 md:px-5 md:pb-5 md:pt-4 flex flex-col gap-1 h-full">
                <h3 class="event-card-title !text-base md:!text-lg !font-semibold !text-gray-900 !leading-tight line-clamp-2">
                    {event.title}
                </h3>
                <p class="event-card-date !text-sm !leading-5 !text-gray-500">
                    {dateTimeLabel}
                </p>
                {#if event.location}
                    <p class="event-card-date !text-sm !leading-5 !text-gray-500 flex items-center gap-1">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {event.location}
                    </p>
                {/if}
            </div>
        {/if}
    </div>
    
    <!-- Right area - content -->
    <div class="w-full md:w-3/5 p-4 md:p-5 flex flex-col justify-between" data-testid="content">
        <div class="flex flex-col gap-2">
            {#if event.imageUrl}
                <!-- With image: show title and date here -->
                <h3 class="event-card-title !text-base md:!text-lg !font-semibold !text-gray-900 !leading-tight line-clamp-2">
                    {event.title}
                </h3>
                <div class="flex items-center gap-2 event-card-date !text-sm !leading-5 !text-gray-500">
                    <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>{dateTimeLabel}</span>
                </div>
                {#if event.location}
                    <div class="flex items-center gap-2 event-card-date !text-sm !leading-5 !text-gray-500">
                        <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{event.location}</span>
                    </div>
                {/if}
            {:else}
                <!-- No image: show title and date on MOBILE only -->
                <div class="md:hidden">
                    <h3 class="event-card-title !text-base !font-semibold !text-gray-900 !leading-tight line-clamp-2">
                        {event.title}
                    </h3>
                    <p class="event-card-date !text-sm !leading-5 !text-gray-500 mt-1">
                        {dateTimeLabel}
                    </p>
                    {#if event.location}
                        <p class="event-card-date !text-sm !leading-5 !text-gray-500 flex items-center gap-1">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            </svg>
                            {event.location}
                        </p>
                    {/if}
                </div>
            {/if}
            
            <!-- Description -->
            <p class="event-card-description !text-sm !leading-6 !text-gray-600 line-clamp-2">
                {getEventDescriptionExcerpt(event.description)}
            </p>
        </div>
        
        <!-- CTA Section - Full width on mobile, inline on desktop -->
        <div class="flex flex-col md:flex-row md:items-center gap-3 pt-3">
            <!--
              Visual-only CTA pill (aria-hidden so screen readers don't read it
              twice -- the card root already exposes the action via aria-label).
              Mirrors PetitionCard.svelte: hover/active states follow the card
              via group-hover/group-active rather than this element having its
              own listeners.
            -->
            <div
                class="event-card-cta-button w-full md:w-auto md:min-w-[110px] md:max-w-[160px] min-h-[48px] md:min-h-[44px] !text-base !leading-6 !transition-all rounded !px-2 !py-1 text-center border border-solid inline-flex items-center {event.isRegistered ? 'event-card-cta-button--registered !bg-emerald-50/80' : '!bg-white group-hover:!bg-[#e5e5e5] group-active:!bg-[#d4d4d4] border-gray-200'}"
                aria-hidden="true"
            >
                <div class="flex w-full justify-center md:justify-between items-center gap-1.5">
                    <span class={event.isRegistered ? 'text-emerald-700 font-medium' : registrationFull ? 'text-gray-500 font-medium' : ''}>
                        {event.isRegistered
                            ? t.View_Details_Button()
                            : registrationFull
                                ? t.Registration_Full_Badge()
                                : t.Register_Button()}
                    </span>
                    <span
                        class="inline-flex items-center flex-shrink-0 {event.isRegistered
                            ? 'text-emerald-600'
                            : 'text-[var(--vv-action-center-button-color,#0d9488)]'}"
                    >
                        <CaretRightIcon aria-hidden="true" />
                    </span>
                </div>
            </div>
            
            <!-- Supporting info: urgency badge or registration availability -->
            <div class="flex items-center gap-3">
                {#if showAvailableSlots}
                    <span class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium whitespace-nowrap bg-gray-100 text-gray-700" role="status">
                        {t.Registration_Slots_Available(event.availableSlots ?? 0)}
                    </span>
                {/if}
                {#if friendlyTime}
                    <span class="event-card-time-badge inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium whitespace-nowrap
                        {friendlyTime.type === 'urgent' ? 'bg-red-100 text-red-700' : 
                         friendlyTime.type === 'soon' ? 'bg-amber-100 text-amber-700' : 
                         'bg-blue-100 text-blue-700'}" role="status">
                        <span>{friendlyTime.label}</span>
                    </span>
                {/if}
            </div>
        </div>
    </div>
</div>

<style>
    :global(.event-card-shell) {
        font-size: 1rem !important;
        line-height: 1.5rem !important;
    }

    :global(.event-card-title) {
        font-size: 1.125rem !important;
        line-height: 1.5rem !important;
        font-weight: 600 !important;
        color: #111827 !important;
    }

    :global(.event-card-date) {
        font-size: 0.875rem !important;
        line-height: 1.25rem !important;
        font-weight: 400 !important;
        color: #6b7280 !important;
    }

    :global(.event-card-description) {
        font-size: 0.875rem !important;
        line-height: 1.5rem !important;
        font-weight: 400 !important;
        color: #4b5563 !important;
    }

    :global(.event-card-cta-button) {
        border: 1px solid #d1d5db !important;
        background-color: #ffffff !important;
        color: #111827 !important;
        box-sizing: border-box !important;
        padding: 0.5rem 0.875rem !important;
        font-size: 1rem !important;
        line-height: 1.5rem !important;
        font-weight: 500 !important;
    }

    :global(.event-card-cta-button:hover) {
        border-color: #d1d5db !important;
    }

    :global(.event-card-cta-button--registered) {
        border-color: #6ee7b7 !important;
        background-color: #ecfdf5 !important;
    }

    :global(.event-card-shell:hover .event-card-cta-button--registered),
    :global(.event-card-shell:active .event-card-cta-button--registered) {
        border-color: #34d399 !important;
        background-color: #d1fae5 !important;
    }

    :global(.event-card-time-badge) {
        font-size: 0.75rem !important;
        line-height: 1rem !important;
        font-weight: 500 !important;
    }

    :global(.event-card-registration-count) {
        font-size: 0.875rem !important;
        line-height: 1.25rem !important;
        font-weight: 400 !important;
        color: #6b7280 !important;
    }
</style>
